import { useState, useEffect, useRef } from 'react';
import '../styles/Confetti.css';

// Fallback to BBQ logo if needed
import bbqLogo from '../assets/bbq-logo.png';

// Import the specific confetti images we found in the folder
import jagermeister from '../assets/confetti/Jagermeister.png';
import shashlik from '../assets/confetti/shashlik.png';

// First approach: try to dynamically import all images from the confetti folder
function importAll(r) {
  return r.keys().map(r);
}

// Use require.context to get all images from the confetti folder
let confettiImageImports = [];
try {
  // This webpack function imports all png, jpg, jpeg, svg files from the confetti folder
  confettiImageImports = importAll(require.context('../assets/confetti', false, /\.(png|jpe?g|svg)$/));
  console.log('Loaded confetti images:', confettiImageImports);
} catch (error) {
  console.error('Error with require.context, trying alternative approach:', error);
}

// Second approach (fallback): use the specific files we know exist
if (confettiImageImports.length === 0) {
  try {
    // We know these files exist from our directory listing
    confettiImageImports = [jagermeister, shashlik];
    console.log('Using specific confetti images:', confettiImageImports);
  } catch (fallbackError) {
    console.error('Error with specific imports:', fallbackError);
  }
}

// Final fallback: if no images were found in the confetti folder, use the BBQ logo
if (confettiImageImports.length === 0) {
  console.log('No confetti images found, using BBQ logo as fallback');
  confettiImageImports = [bbqLogo];
}

const Confetti = ({ active, duration = 3000 }) => {
    const [isActive, setIsActive] = useState(active);
    const containerRef = useRef(null);
    const confettiItems = useRef([]);
    
    // Number of confetti pieces - INCREASED FOR MORE IMPACT
    const confettiCount = 80; // Increased from 50 to 80
    
    useEffect(() => {
        setIsActive(active);
        
        if (active) {
            createConfetti();
            
            // Stop the animation after the duration
            const timer = setTimeout(() => {
                setIsActive(false);
            }, duration);
            
            return () => clearTimeout(timer);
        }
    }, [active, duration]);
    
    const createConfetti = () => {
        if (!containerRef.current) return;
        
        confettiItems.current = [];
        const container = containerRef.current;
        container.innerHTML = '';
        
        // Create the confetti elements
        for (let i = 0; i < confettiCount; i++) {
            const confetti = document.createElement('div');
            confetti.className = 'confetti-item';
            
            // Randomize confetti properties - use any image from the imports
            const randomIndex = Math.floor(Math.random() * confettiImageImports.length);
            const imgSrc = confettiImageImports[randomIndex];
            
            const img = document.createElement('img');
            img.src = typeof imgSrc === 'string' ? imgSrc : imgSrc.default || imgSrc;
            img.alt = 'confetti';
            confetti.appendChild(img);
            
            // Random initial position
            const startX = Math.random() * 100;
            const startY = -20 - Math.random() * 80; // Start above the viewport
            
            // Random size - MUCH BIGGER NOW
            const size = 40 + Math.random() * 60; // Increased from 15-40px to 40-100px
            
            // Random rotation and speed
            const rotation = Math.random() * 360;
            const duration = 2 + Math.random() * 4; // Increased from 1-3s to 2-6s
            const delay = Math.random() * 3; // Increased from 0-1.5s to 0-3s
            
            // Apply styles
            confetti.style.left = `${startX}%`;
            confetti.style.top = `${startY}%`;
            confetti.style.width = `${size}px`;
            confetti.style.height = `${size}px`;
            confetti.style.transform = `rotate(${rotation}deg)`;
            confetti.style.animationDuration = `${duration}s`;
            confetti.style.animationDelay = `${delay}s`;
            
            container.appendChild(confetti);
            confettiItems.current.push(confetti);
        }
    };
    
    return (
        <div 
            ref={containerRef} 
            className={`confetti-container ${isActive ? 'active' : ''}`}
        />
    );
};

export default Confetti; 